import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { LowStockAlerts } from "@/components/Dashboard/LowStockAlerts";
import { MetricCard } from "@/components/Dashboard/MetricCard";
import { AlertCard } from "@/components/Dashboard/AlertCard";
import { DashboardWidget } from "@/components/Dashboard/DashboardWidget";
import { DashboardConfigDialog } from "@/components/Dashboard/DashboardConfigDialog";
import { QuickAccessPanel } from "@/components/Dashboard/QuickAccessPanel";
import { Package, Activity, Users, TrendingUp, RefreshCw, Layout, Filter } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/contexts/LanguageContext";
import { useRealtimeMetrics } from "@/hooks/useRealtimeMetrics";
import { useMetricsHistory } from "@/hooks/useMetricsHistory";
import { useKPIMonitoring } from "@/hooks/useKPIMonitoring";
import { useDashboardConfig, WidgetLayout } from "@/hooks/useDashboardConfig";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const CustomizableDashboard = () => {
  const { t } = useLanguage();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [areaFilter, setAreaFilter] = useState<string>("all");
  const { metrics, loading: metricsLoading, refresh } = useRealtimeMetrics(areaFilter);
  const { history, loading: historyLoading } = useMetricsHistory(areaFilter);
  useKPIMonitoring(metrics);

  const {
    configs,
    currentConfig,
    loading: configLoading,
    saveConfig,
    updateConfig,
    deleteConfig,
    setDefaultConfig,
    setCurrentConfig,
  } = useDashboardConfig();

  const [visibleWidgets, setVisibleWidgets] = useState<string[]>([]);
  const [layout, setLayout] = useState<WidgetLayout[]>([]);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (currentConfig) {
      setVisibleWidgets(currentConfig.visible_widgets);
      setLayout(currentConfig.layout);
    }
  }, [currentConfig]);

  const handleLayoutChange = (newLayout: WidgetLayout[]) => {
    setLayout(newLayout);
    if (currentConfig && editMode) {
      updateConfig(currentConfig.id, newLayout, visibleWidgets);
    }
  };

  const handleToggleWidget = (widgetId: string) => {
    setVisibleWidgets((prev) => {
      if (prev.includes(widgetId)) {
        return prev.filter((id) => id !== widgetId);
      }
      return [...prev, widgetId];
    });
  };

  const renderWidget = (widgetId: string) => {
    switch (widgetId) {
      case "active_orders":
        return (
          <DashboardWidget>
            <MetricCard
              title={t.dashboard?.activeOrders || "Órdenes Activas"}
              value={metrics.activeOrders}
              change={metrics.activeOrders > 0 ? `${t.dashboard?.inProgress || "En progreso"}` : `${t.dashboard?.noActive || "Sin órdenes"}`}
              trend={metrics.activeOrders > 0 ? "up" : "neutral"}
              icon={Package}
              iconColor="text-primary"
            />
          </DashboardWidget>
        );
      case "in_progress_processes":
        return (
          <DashboardWidget>
            <MetricCard
              title={t.dashboard?.inProgressProcesses || "Procesos en Curso"}
              value={metrics.inProgressProcesses}
              change={metrics.inProgressProcesses > 0 ? `${t.dashboard?.active || "Activos"}` : `${t.dashboard?.noActive || "Sin procesos"}`}
              trend={metrics.inProgressProcesses > 0 ? "up" : "neutral"}
              icon={Activity}
              iconColor="text-accent"
            />
          </DashboardWidget>
        );
      case "active_operators":
        return (
          <DashboardWidget>
            <MetricCard
              title={t.dashboard?.activeOperators || "Operarios Trabajando"}
              value={metrics.activeOperators}
              change={metrics.activeOperators > 0 ? `${t.dashboard?.workingNow || "Trabajando ahora"}` : `${t.dashboard?.noActive || "Sin operarios"}`}
              trend={metrics.activeOperators > 0 ? "up" : "neutral"}
              icon={Users}
              iconColor="text-chart-3"
            />
          </DashboardWidget>
        );
      case "completion_rate":
        return (
          <DashboardWidget>
            <MetricCard
              title={t.dashboard?.completionRate || "Tasa de Finalización"}
              value={`${metrics.completionRate}%`}
              change={`${t.dashboard?.today || "Hoy"}`}
              trend={metrics.completionRate >= 80 ? "up" : metrics.completionRate >= 50 ? "neutral" : "down"}
              icon={TrendingUp}
              iconColor="text-accent"
            />
          </DashboardWidget>
        );
      case "alerts":
        return (
          <DashboardWidget>
            <AlertCard alerts={metrics.alerts} />
          </DashboardWidget>
        );
      case "realtime_chart":
        return (
          <DashboardWidget>
            <Card className="h-full">
              <CardHeader>
                <CardTitle>{t.dashboard.realtimeEvolution || "Evolución en Tiempo Real"}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={history}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="activeOrders"
                      stackId="1"
                      stroke="hsl(var(--primary))"
                      fill="hsl(var(--primary))"
                      name={t.dashboard?.activeOrders || "Órdenes"}
                    />
                    <Area
                      type="monotone"
                      dataKey="inProgressProcesses"
                      stackId="1"
                      stroke="hsl(var(--accent))"
                      fill="hsl(var(--accent))"
                      name={t.dashboard?.inProgressProcesses || "Procesos"}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </DashboardWidget>
        );
      case "performance_chart":
        return (
          <DashboardWidget>
            <Card className="h-full">
              <CardHeader>
                <CardTitle>{t.dashboard.performanceMetrics || "Métricas de Rendimiento"}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={history}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="activeOperators"
                      stroke="hsl(var(--chart-3))"
                      name={t.dashboard?.activeOperators || "Operarios"}
                    />
                    <Line
                      type="monotone"
                      dataKey="completionRate"
                      stroke="hsl(var(--accent))"
                      name={t.dashboard?.completionRate || "Completitud %"}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </DashboardWidget>
        );
      case "personal_stats":
        return (
          <DashboardWidget>
            {/* <PersonalStatsWidget /> */}
            <p className="text-muted-foreground">Personal Stats Widget no disponible</p>
          </DashboardWidget>
        );
      case "referencias_stats":
        return (
          <DashboardWidget>
            {/* <ReferenciasStatsWidget /> */}
            <p className="text-muted-foreground">Referencias Stats Widget no disponible</p>
          </DashboardWidget>
        );
      case "inventario_general":
        return (
          <DashboardWidget>
            {/* <InventarioWidget /> */}
            <p className="text-muted-foreground">Inventario Widget no disponible</p>
          </DashboardWidget>
        );
      case "eventos_proximos":
        return (
          <DashboardWidget>
            {/* <EventosWidget /> */}
            <p className="text-muted-foreground">Eventos Widget no disponible</p>
          </DashboardWidget>
        );
      default:
        return null;
    }
  };

  if (configLoading || metricsLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-12 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-[140px] rounded-lg" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-3 sm:space-y-4 md:space-y-6">
        <LowStockAlerts />
        <div className="flex flex-col gap-3 sm:gap-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-foreground truncate">{t.dashboard.welcome}</h2>
              <p className="text-xs sm:text-sm md:text-base text-muted-foreground mt-0.5 sm:mt-1 truncate">
                {currentConfig?.name || t.dashboard.subtitle}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              disabled={metricsLoading}
              className="flex-shrink-0 h-8 w-8 sm:h-9 sm:w-auto sm:px-3 p-0"
            >
              <RefreshCw className={`w-4 h-4 ${metricsLoading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline ml-2">{t.dashboard?.refresh || "Actualizar"}</span>
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={areaFilter} onValueChange={setAreaFilter}>
              <SelectTrigger className="w-[140px] sm:w-[180px] h-8 sm:h-9 text-xs sm:text-sm">
                <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                <SelectValue placeholder={t.filters.selectArea} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.filters.allAreas}</SelectItem>
                <SelectItem value="corte_plotter">{t.auth.areas.corte_plotter}</SelectItem>
                <SelectItem value="laminador">{t.auth.areas.laminador}</SelectItem>
                <SelectItem value="taller">{t.auth.areas.taller}</SelectItem>
                <SelectItem value="generico">{t.auth.areas.generico}</SelectItem>
              </SelectContent>
            </Select>
            {!isMobile && (
              <>
                <Button
                  variant={editMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEditMode(!editMode)}
                  className="gap-2 h-8 sm:h-9"
                >
                  <Layout className="w-4 h-4" />
                  {editMode ? t.dashboard?.exitEditMode || "Salir" : t.dashboard?.editMode || "Editar"}
                </Button>
                <DashboardConfigDialog
                  configs={configs}
                  currentConfig={currentConfig}
                  onSave={saveConfig}
                  onDelete={deleteConfig}
                  onSetDefault={setDefaultConfig}
                  onSelectConfig={setCurrentConfig}
                  visibleWidgets={visibleWidgets}
                  onToggleWidget={handleToggleWidget}
                />
              </>
            )}
          </div>
        </div>

        {/* Panel de Accesos Rápidos */}
        <QuickAccessPanel />

        {editMode && (
          <div className="p-4 bg-primary/10 border border-primary rounded-lg">
            <p className="text-sm text-foreground">
              {t.dashboard?.editModeDesc || "Modo de edición activado: arrastra los widgets para reorganizarlos"}
            </p>
          </div>
        )}

        {/* Mobile: Simple grid layout */}
        {isMobile ? (
          <div className="grid grid-cols-1 gap-2 sm:gap-3">
            {visibleWidgets.map((widgetId) => (
              <div key={widgetId}>
                {renderWidget(widgetId)}
              </div>
            ))}
          </div>
        ) : (
          /* Desktop: Draggable grid layout */
          <GridLayout
            className="layout"
            layout={layout}
            cols={12}
            rowHeight={60}
            width={1200}
            onLayoutChange={handleLayoutChange}
            isDraggable={editMode}
            isResizable={editMode}
            compactType="vertical"
            preventCollision={false}
          >
            {visibleWidgets.map((widgetId) => (
              <div key={widgetId}>
                {renderWidget(widgetId)}
              </div>
            ))}
          </GridLayout>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CustomizableDashboard;
